// Dev-only dispatcher for the onboarding funnel — `GET /v1/onboarding/state`
// and `POST /v1/onboarding/advance`, which answer the same body.
//
// Split out of `mocks.ts` (plan 101 slice 15 step 4). `null` means "not mine"
// and `mockFetch` falls through.
//
// `state: 'active'` was HARDCODED and served for both doors, so all four
// `e2e/onboarding.spec.ts` cases only ever saw the terminal screen — and every
// step BEFORE it was undrivable in a browser. Two of them were dead ends in
// prod (`new` had no branch at all, `yml_requested` had no control) and a human
// looking at either screen once would have caught both.
//
// `?mock_onboarding=<state>` picks the step, following `mock_unpaid` /
// `mock_signed_out` / `mock_seat_cap` / `mock_ssh_down`. An unrecognised value
// falls back to `active` rather than 400-ing: this is a story selector, not an
// edge, and a typo that silently shows the default screen is better than a mock
// that throws where the real route never would.
//
// The body is `OnboardingStatus`, and it is DERIVED the way `onboardingView`
// (`apps/api/src/deps/onboarding.ts`) derives it — `nextFunnelMove(state)` and
// `isComplete(state)` from the same domain module the API calls. It used to
// restate the shape instead, which is how it went on serving `next_event: null`
// after that field was renamed to `next_move` (#4611): a literal body is
// checked by nothing. Annotating it makes the next rename a type error here.

import { isComplete, nextFunnelMove, OnboardingState } from '@developerz/domain';
import { me } from './mock-session';
import type { OnboardingStatus } from './onboarding';

/**
 * DERIVED from the enum, never a hand-kept copy of it — which is the same rule
 * the header above states and this list had already broken: epic #3629 added
 * `box_attached` and `mcp_connected` to `OnboardingState`, and a literal array
 * silently fell back to `active` for both, leaving the two NEW steps exactly as
 * undrivable in a browser as the two dead ends this selector was written for.
 */
const ONBOARDING_STATES: readonly OnboardingState[] = Object.values(OnboardingState);

/** `?mock_onboarding=` → a funnel state, defaulting to the finished account. */
function onboardingState(): OnboardingState {
  const asked = new URLSearchParams(window.location.search).get('mock_onboarding');
  const match = ONBOARDING_STATES.find((s) => s === asked);
  return match ?? OnboardingState.Active;
}

/** Serve both funnel doors; `null` for anything else. */
export function onboardingMock(path: string): Promise<OnboardingStatus> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare !== '/v1/onboarding/state' && bare !== '/v1/onboarding/advance') return null;
  const state = onboardingState();
  return Promise.resolve({
    account_id: me.id,
    state,
    next_move: nextFunnelMove(state),
    complete: isComplete(state),
    // The mock walks the stored ladder: no facts, so each card shows as its state names.
    facts: null,
  });
}
